import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Users, Handshake, TrendingUp } from 'lucide-react';
import { FadeInUp } from '../components/animations';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schema';

const AboutPage = () => {
  const values = [
    { icon: <Globe size={32} strokeWidth={1.5} />, title: 'Global Reach', description: 'We connect international buyers with Indian manufacturers across multiple product categories, bridging the gap between global requirements and Indian manufacturing capabilities.' },
    { icon: <Users size={32} strokeWidth={1.5} />, title: 'Buyer-Focused', description: 'Every sourcing engagement begins with understanding the buyer\'s specific product, quality, quantity and commercial requirements.' },
    { icon: <Handshake size={32} strokeWidth={1.5} />, title: 'Long-Term Relationships', description: 'We focus on building sustainable buyer\u2013manufacturer relationships that create value for both parties over time.' },
    { icon: <TrendingUp size={32} strokeWidth={1.5} />, title: 'Mutual Growth', description: 'We believe in creating opportunities that benefit both international buyers and Indian manufacturers through reliable sourcing coordination.' },
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      <SEO
        title="About Taiga Sourcing | India Sourcing Company"
        description="Taiga Sourcing is an India-based sourcing company connecting international buyers with Indian manufacturers across multiple product categories."
        keywords="about Taiga Sourcing, India sourcing company, India sourcing partner, about us India sourcing"
        path="/about"
        schema={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])}
      />
      <section className="relative py-24 lg:py-32 bg-[#071E45] text-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <p className="text-sm tracking-[0.3em] uppercase text-white/50 mb-4 font-medium">About Us</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="about-title">
              BRIDGING GLOBAL BUYERS<br />WITH INDIAN MANUFACTURERS
            </h1>
            <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
              Multi-category sourcing from India. One partner for your entire sourcing journey.
            </p>
          </FadeInUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white" data-testid="company-intro">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <FadeInUp>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
                WHO WE ARE
              </h2>
              <div className="space-y-4 text-base text-[#6B7280] leading-relaxed text-left">
                <p>
                  Taiga Sourcing is an India-based sourcing company that works with international buyers to identify suitable Indian manufacturers and coordinate the sourcing process from product requirement to shipment.
                </p>
                <p>
                  We source across multiple product categories including apparel, tiles, bathware, home decor, furniture, textiles, handicrafts, packaging and more. Our role is to understand the buyer's requirement, find the right manufacturing match in India, and coordinate the entire process.
                </p>
                <p>
                  Whether you are a fashion brand, importer, retailer, hospitality company or any business looking to source products from India, Taiga Sourcing provides a single point of contact for your sourcing needs.
                </p>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#fafaf9]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeInUp className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              OUR APPROACH
            </h2>
          </FadeInUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((value, i) => (
              <FadeInUp key={i} delay={i * 0.1}>
                <div className="p-8 bg-white border border-[#E5E0D8]/60" data-testid={`value-card-${i}`}>
                  <div className="text-[#1E3D66] mb-4">{value.icon}</div>
                  <h3 className="text-sm font-semibold tracking-wider uppercase text-[#0B2A57] mb-3">{value.title}</h3>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{value.description}</p>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#071E45] text-white" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              LET'S WORK TOGETHER.
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8">
              Whether you're looking to source products from India or register as a manufacturer, we'd like to hear from you.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/request-sourcing"
                className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0B2A57] text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-xl hover:-translate-y-0.5"
              >
                Request Sourcing <ArrowRight size={18} />
              </Link>
              <Link
                to="/for-suppliers"
                className="inline-flex items-center gap-2 px-10 py-4 border border-white/30 text-white text-sm font-semibold tracking-wider uppercase transition-all hover:bg-white/10"
              >
                Register as Supplier
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
